import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { PLANE_MODEL_URL, PLANE_RENDER, DEFAULT_LOCATION } from './constants.js';
import { isMobileDevice } from './mobile-controls.js';
import { initSkySystem, updateSky } from './sky.js';
import { createRenderer, RendererType } from './renderer/renderer-factory.js';
import { setDeferredDisposal } from './renderer/texture-disposal.js';
import {
  clampMercatorLatitude,
  normalizeGeoCoordinates,
  normalizeLongitude,
  shortestLongitudeDelta,
} from './geo.js';

// Export for use in buildings.ts
export { BufferGeometryUtils };

// Re-export RendererType for external use
export type { RendererType };

// Scene components
let scene: THREE.Scene | null = null;
let camera: THREE.PerspectiveCamera | null = null;
let renderer: THREE.WebGLRenderer | null = null;
let rendererType: RendererType = 'webgl';
let planeModel: THREE.Group | null = null;
const planeMeshes = new Map<string, THREE.Object3D>(); // id -> mesh
const propellerMeshes = new Map<string, THREE.Object3D>(); // id -> propeller node

// World origin (for geo to world conversion)
let originLng: number = DEFAULT_LOCATION.lng;
let originLat: number = DEFAULT_LOCATION.lat;

// Constants for geo conversion
const METERS_PER_DEGREE_LAT = 111320;
const metersPerDegreeLng = (lat: number): number => 111320 * Math.cos(lat * Math.PI / 180);

// Types
export interface WorldCoords {
  x: number;
  y: number;
  z: number;
}

export interface GeoCoords {
  lng: number;
  lat: number;
  alt: number;
}

export interface PlaneState {
  id: string;
  lat: number;
  lng: number;
  altitude: number;
  heading: number;
  pitch: number;
  roll: number;
  speed: number;
  color: string;
  name?: string;
}

/**
 * Convert geo coordinates to world coordinates
 */
export function geoToWorld(lng: number, lat: number, alt: number): WorldCoords {
  return {
    x: shortestLongitudeDelta(originLng, lng) * metersPerDegreeLng(originLat),
    y: alt,
    z: -(clampMercatorLatitude(lat) - originLat) * METERS_PER_DEGREE_LAT
  };
}

/**
 * Convert world coordinates back to geo
 */
export function worldToGeo(x: number, y: number, z: number): GeoCoords {
  return {
    lng: normalizeLongitude(originLng + x / metersPerDegreeLng(originLat)),
    lat: clampMercatorLatitude(originLat - z / METERS_PER_DEGREE_LAT),
    alt: y
  };
}

/**
 * Set the world origin for coordinate conversion
 */
export function setOrigin(lng: number, lat: number): void {
  const normalized = normalizeGeoCoordinates(lng, lat);
  originLng = normalized.lng;
  originLat = normalized.lat;
}

/**
 * Get current origin
 */
export function getOrigin(): { lng: number; lat: number } {
  return { lng: originLng, lat: originLat };
}

/**
 * Get scene origin with conversion factors for geometry workers
 * This provides all the info needed to convert geo coords to world coords in a worker
 */
export function getSceneOriginForWorker(): {
  lng: number;
  lat: number;
  metersPerDegLng: number;
  metersPerDegLat: number;
} {
  return {
    lng: originLng,
    lat: originLat,
    metersPerDegLng: metersPerDegreeLng(originLat),
    metersPerDegLat: METERS_PER_DEGREE_LAT,
  };
}

/**
 * Initialize the Three.js scene
 */
export async function initScene(): Promise<{
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
}> {
  // Create scene
  scene = new THREE.Scene();
  // Note: Sky dome replaces solid color background (set to null by initSkySystem)

  // Create camera
  const aspect = window.innerWidth / window.innerHeight;
  camera = new THREE.PerspectiveCamera(60, aspect, 1, 100000);
  camera.position.set(0, 500, 500);
  camera.lookAt(0, 0, 0);

  // Create renderer with WebGPU preference and WebGL fallback
  // Use ?webgpu=0 URL param to force WebGL for debugging
  const rendererResult = await createRenderer({ antialias: false });
  renderer = rendererResult.renderer;
  rendererType = rendererResult.type;

  // Enable deferred texture disposal for WebGPU to avoid command buffer conflicts
  if (rendererType === 'webgpu') {
    setDeferredDisposal(true);
  }

  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.shadowMap.enabled = true;
  // PCFSoftShadowMap only supported in WebGL; WebGPU uses default shadow type
  if (rendererType === 'webgl') {
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  }

  // Replace map container content with Three.js canvas
  const container = document.getElementById('map');
  if (container) {
    container.innerHTML = '';
    container.appendChild(renderer.domElement);
  }

  // Lighting
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
  scene.add(ambientLight);

  const sunLight = new THREE.DirectionalLight(0xffffff, 0.8);
  sunLight.position.set(1000, 2000, 1000);
  sunLight.castShadow = true;
  sunLight.shadow.mapSize.width = 1024;  // Reduced from 2048 for performance
  sunLight.shadow.mapSize.height = 1024; // Reduced from 2048 for performance
  sunLight.shadow.camera.near = 100;
  sunLight.shadow.camera.far = 10000;
  sunLight.shadow.camera.left = -5000;
  sunLight.shadow.camera.right = 5000;
  sunLight.shadow.camera.top = 5000;
  sunLight.shadow.camera.bottom = -5000;
  // Add shadow bias to reduce shadow acne artifacts
  sunLight.shadow.bias = -0.0005;
  sunLight.shadow.normalBias = 0.02;
  scene.add(sunLight);

  // Initialize procedural sky system with atmospheric effects
  // Pass renderer type for WebGL/WebGPU shader compatibility
  await initSkySystem(scene, {
    sunPosition: sunLight.position.clone(),
    turbidity: 2,
    cloudDensity: 0.35,
    cloudAltitude: 3000,
    fogDensity: 0.00006,
    rendererType: rendererType
  });

  // Handle window resize
  window.addEventListener('resize', () => {
    if (camera && renderer) {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    }
  });

  // Load plane model
  await loadPlaneModel();

  return { scene, camera, renderer };
}

/**
 * Load the plane GLTF model
 */
async function loadPlaneModel(): Promise<THREE.Group> {
  return new Promise((resolve) => {
    const loader = new GLTFLoader();

    // Configure Draco decoder for compressed models
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      PLANE_MODEL_URL,
      (gltf) => {
        planeModel = gltf.scene;
        // Configure mesh settings while preserving original textures
        planeModel.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            mesh.castShadow = false;
            // Disable receiving shadows to prevent shadow acne artifacts
            // when flying through building shadows
            mesh.receiveShadow = false;
          }
        });
        resolve(planeModel);
      },
      undefined,
      (error) => {
        console.error('Error loading plane model:', error);
        // Create a fallback box geometry
        planeModel = createFallbackPlane();
        resolve(planeModel);
      }
    );
  });
}

/**
 * Create a fallback plane mesh if GLTF fails to load
 * Uses merged geometry to prevent z-fighting between overlapping parts
 */
function createFallbackPlane(): THREE.Group {
  const group = new THREE.Group();

  // Default white/silver materials (will be replaced when cloned for player)
  const fuselageMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.4,
    metalness: 0.6
  });
  const wingMaterial = new THREE.MeshStandardMaterial({
    color: 0xf0f0f0,
    roughness: 0.45,
    metalness: 0.5
  });

  // Collect geometries for merging to prevent z-fighting
  const fuselageGeometries: THREE.BufferGeometry[] = [];
  const wingGeometries: THREE.BufferGeometry[] = [];

  // Fuselage - higher resolution cylinder for smoother appearance
  const fuselageGeometry = new THREE.CylinderGeometry(2, 1.5, 20, 16);
  fuselageGeometry.rotateZ(Math.PI / 2);
  fuselageGeometries.push(fuselageGeometry);

  // Nose cone
  const noseGeometry = new THREE.ConeGeometry(1.5, 4, 16);
  noseGeometry.rotateZ(-Math.PI / 2);
  noseGeometry.translate(12, 0, 0);
  fuselageGeometries.push(noseGeometry);

  // Wings - split into left and right to avoid passing through fuselage
  // Fuselage radius is ~2, so wings start at z=±2.5 to avoid overlap
  const wingSpan = 11.5; // Each wing segment length (from fuselage edge to tip)
  const wingOffset = 2.5; // Start wings outside fuselage radius
  // Wing ends at: wingOffset + wingSpan = 2.5 + 11.5 = 14

  // Left wing segment
  const leftWingGeometry = new THREE.BoxGeometry(4, 0.3, wingSpan);
  leftWingGeometry.translate(0, 0, wingOffset + wingSpan / 2);
  wingGeometries.push(leftWingGeometry);

  // Right wing segment
  const rightWingGeometry = new THREE.BoxGeometry(4, 0.3, wingSpan);
  rightWingGeometry.translate(0, 0, -(wingOffset + wingSpan / 2));
  wingGeometries.push(rightWingGeometry);

  // Wing root fairings (smooth transition from fuselage to wing)
  const leftFairingGeometry = new THREE.BoxGeometry(3, 0.8, 3);
  leftFairingGeometry.translate(0, 0.3, 2);
  fuselageGeometries.push(leftFairingGeometry);

  const rightFairingGeometry = new THREE.BoxGeometry(3, 0.8, 3);
  rightFairingGeometry.translate(0, 0.3, -2);
  fuselageGeometries.push(rightFairingGeometry);

  // Wing tips (angled) - positioned at z=±14 to align with wing segment ends
  const leftWingTipGeo = new THREE.BoxGeometry(2, 0.2, 4);
  leftWingTipGeo.rotateX(Math.PI / 6);
  leftWingTipGeo.translate(0, 0.5, 14);
  wingGeometries.push(leftWingTipGeo);

  const rightWingTipGeo = new THREE.BoxGeometry(2, 0.2, 4);
  rightWingTipGeo.rotateX(-Math.PI / 6);
  rightWingTipGeo.translate(0, 0.5, -14);
  wingGeometries.push(rightWingTipGeo);

  // Tail horizontal stabilizer - positioned at z=±2.35 to close gap with vertical stabilizer
  // Vertical stabilizer is 0.3 thick at z=0, so edges are at z=±0.15
  // Tail segments with depth 4.7 at z=±2.35 extend from z=0 to z=4.7
  const leftTailGeometry = new THREE.BoxGeometry(3, 0.2, 4.7);
  leftTailGeometry.translate(-9, 0, 2.35);
  wingGeometries.push(leftTailGeometry);

  const rightTailGeometry = new THREE.BoxGeometry(3, 0.2, 4.7);
  rightTailGeometry.translate(-9, 0, -2.35);
  wingGeometries.push(rightTailGeometry);

  // Vertical stabilizer
  const stabilizerGeometry = new THREE.BoxGeometry(4, 5, 0.3);
  stabilizerGeometry.translate(-8, 2.5, 0);
  wingGeometries.push(stabilizerGeometry);

  // Engine nacelles (under wings)
  const leftEngineGeo = new THREE.CylinderGeometry(0.8, 1, 3, 12);
  leftEngineGeo.rotateZ(Math.PI / 2);
  leftEngineGeo.translate(1, -1, 6);
  fuselageGeometries.push(leftEngineGeo);

  const rightEngineGeo = new THREE.CylinderGeometry(0.8, 1, 3, 12);
  rightEngineGeo.rotateZ(Math.PI / 2);
  rightEngineGeo.translate(1, -1, -6);
  fuselageGeometries.push(rightEngineGeo);

  // Merge geometries into single meshes to eliminate z-fighting
  try {
    const mergedFuselageGeometry = BufferGeometryUtils.mergeGeometries(fuselageGeometries, false);
    if (mergedFuselageGeometry) {
      const fuselage = new THREE.Mesh(mergedFuselageGeometry, fuselageMaterial);
      fuselage.name = 'fuselage';
      group.add(fuselage);
    }

    const mergedWingGeometry = BufferGeometryUtils.mergeGeometries(wingGeometries, false);
    if (mergedWingGeometry) {
      const wings = new THREE.Mesh(mergedWingGeometry, wingMaterial);
      wings.name = 'wings';
      group.add(wings);
    }

    // Dispose individual geometries to free GPU memory
    fuselageGeometries.forEach(geo => geo.dispose());
    wingGeometries.forEach(geo => geo.dispose());
  } catch (error) {
    console.error('Failed to merge plane geometries:', error);
    // Fallback: create simple box if merge fails
    const fallbackGeo = new THREE.BoxGeometry(20, 2, 28);
    const fallbackMesh = new THREE.Mesh(fallbackGeo, fuselageMaterial);
    fallbackMesh.name = 'fuselage';
    group.add(fallbackMesh);
  }

  return group;
}

/**
 * Get or create a plane mesh for a player
 */
function getOrCreatePlaneMesh(id: string, _color: string): THREE.Object3D | null {
  if (planeMeshes.has(id)) {
    return planeMeshes.get(id)!;
  }

  if (!planeModel) {
    console.warn('Plane model not loaded yet');
    return null;
  }

  const mesh = planeModel.clone();
  const scale = isMobileDevice() ? PLANE_RENDER.MOBILE_SCALE : PLANE_RENDER.SCALE;
  mesh.scale.set(scale, scale, scale);

  // Find and track the propeller node for animation
  // Original mesh name: 'Body_MAT_0.001' -> Three.js strips dots -> 'Body_MAT_0001'
  // Early return once found to capture only the first matching propeller mesh
  mesh.traverse((child) => {
    if (propellerMeshes.has(id)) return;
    const meshChild = child as THREE.Mesh;
    if (meshChild.isMesh && child.name.includes('Body_MAT_0001')) {
      propellerMeshes.set(id, child);
    }
  });

  if (scene) {
    scene.add(mesh);
  }
  planeMeshes.set(id, mesh);
  return mesh;
}

// Propeller rotation speed constant (radians per second at cruise speed ~50 m/s)
const PROPELLER_BASE_SPEED = 40; // radians per second base rotation

/**
 * Update a plane's position and rotation
 */
export function updatePlaneMesh(planeState: PlaneState, id: string, color: string, deltaTime: number = 0.016): void {
  const mesh = getOrCreatePlaneMesh(id, color);
  if (!mesh) return;

  // Convert geo to world coordinates
  const worldPos = geoToWorld(planeState.lng, planeState.lat, planeState.altitude);
  mesh.position.set(worldPos.x, worldPos.y, worldPos.z);

  // Apply rotation (heading, pitch, roll)
  // Reset rotation first
  mesh.rotation.set(0, 0, 0);

  // Apply rotations in order: yaw (heading), pitch, roll
  // Three.js uses Y-up, so:
  // - Heading (yaw) rotates around Y axis
  // - Pitch rotates around X axis (nose up/down)
  // - Roll rotates around Z axis (banking)

  const headingRad = THREE.MathUtils.degToRad(-planeState.heading + 90 + 90); // +90 to rotate model CCW
  const pitchRad = THREE.MathUtils.degToRad(-planeState.pitch); // Negated - positive pitch = nose up visually
  const rollRad = THREE.MathUtils.degToRad(planeState.roll);

  // Use Euler with YXZ order for flight sim rotations
  mesh.rotation.order = 'YXZ';
  mesh.rotation.y = headingRad;
  mesh.rotation.x = pitchRad;
  mesh.rotation.z = rollRad;

  // Animate propeller based on speed
  const propeller = propellerMeshes.get(id);
  if (propeller) {
    // Propeller spins faster with speed, minimum spin when moving
    const speedFactor = Math.max(0.2, planeState.speed / 50); // Normalize to cruise speed
    const rotationSpeed = PROPELLER_BASE_SPEED * speedFactor;
    // Rotate around Z axis (forward axis in model space) so blades spin in a circle
    propeller.rotation.z += rotationSpeed * deltaTime;
  }
}

/**
 * Remove a plane mesh
 */
export function removePlaneMesh(id: string): void {
  const mesh = planeMeshes.get(id);
  if (mesh && scene) {
    scene.remove(mesh);
    planeMeshes.delete(id);
    propellerMeshes.delete(id);
  }
}

/**
 * Get the Three.js scene
 */
export function getScene(): THREE.Scene | null {
  return scene;
}

/**
 * Get the Three.js camera
 */
export function getCamera(): THREE.PerspectiveCamera | null {
  return camera;
}

/**
 * Get the Three.js renderer
 */
export function getRenderer(): THREE.WebGLRenderer | null {
  return renderer;
}

/**
 * Get the renderer type (webgpu or webgl)
 */
export function getRendererType(): RendererType {
  return rendererType;
}

/**
 * Update the sky system (call each frame)
 */
export function updateSkySystem(deltaTime: number): void {
  if (camera) {
    updateSky(deltaTime, camera.position);
  }
}

/**
 * Render the scene
 */
export function render(): void {
  if (renderer && scene && camera) {
    renderer.render(scene, camera);
  }
}
